/**
 * Invite Group Dialog
 * Allows group admins/owners to invite users via share code or direct invite
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  CircularProgress,
  InputAdornment,
} from '@mui/material'
import {
  ContentCopy,
  Share,
  Delete,
  PersonAdd,
  Search as SearchIcon,
  Close,
  Link as LinkIcon,
  CalendarMonth,
  Numbers,
  Person,
} from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { socialApi } from '@api/endpoints/social'
import type { GroupDetailsDto, GenerateShareCodeRequest } from '@api/types/social'
import { profileApi } from '@api/endpoints/profile'
import { ThemedQRCode } from '@/components/ThemedQRCode'

interface InviteGroupDialogProps {
  open: boolean
  onClose: () => void
  group: GroupDetailsDto
}

export function InviteGroupDialog({ open, onClose, group }: InviteGroupDialogProps) {
  const [activeTab, setActiveTab] = useState(0)
  const [copiedShareCode, setCopiedShareCode] = useState(false)
  const queryClient = useQueryClient()

  // Share Code Tab State
  const [maxUses, setMaxUses] = useState<number | ''>('')
  const [expirationDays, setExpirationDays] = useState<number | ''>('')
  const [generatedShareCode, setGeneratedShareCode] = useState<{
    shareCode: string
    maxUses: number | null
    expirationDate: string | null
    usedCount: number
  } | null>(null)

  // Direct Invite Tab State
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

  // Fetch friends for direct invite
  const { data: friends, isLoading: isLoadingFriends } = useQuery({
    queryKey: ['friends'],
    queryFn: () => socialApi.getFriends(),
    enabled: activeTab === 1 && open,
  })

  // Fetch active share codes for the group (admin/owner only)
  const { data: activeShareCodes, isLoading: isLoadingShareCodes } = useQuery({
    queryKey: ['groupShareCodes', group.id],
    queryFn: () => socialApi.getGroupShareCodes(group.id),
    enabled: activeTab === 0 && open,
  })

  // Generate share code mutation
  const generateShareCodeMutation = useMutation({
    mutationFn: (request: GenerateShareCodeRequest) =>
      socialApi.generateShareCode(group.id, request),
    onSuccess: (data) => {
      // Store the generated share code data locally for immediate display
      setGeneratedShareCode(data)
      queryClient.invalidateQueries({ queryKey: ['group', group.id] })
      queryClient.invalidateQueries({ queryKey: ['groupShareCodes', group.id] })
      setMaxUses('')
      setExpirationDays('')
    },
  })

  // Revoke the code on screen. Takes the code because the server deactivates a specific one
  // — the old group-wide revoke called a route that does not exist and always failed.
  const revokeShareCodeMutation = useMutation({
    mutationFn: (shareCode: string) => socialApi.deactivateShareCode(group.id, shareCode),
    onSuccess: () => {
      // Clear local share code state
      setGeneratedShareCode(null)
      queryClient.invalidateQueries({ queryKey: ['group', group.id] })
      queryClient.invalidateQueries({ queryKey: ['groupShareCodes', group.id] })
    },
  })

  // Deactivate a specific share code mutation
  const deactivateShareCodeMutation = useMutation({
    mutationFn: (shareCode: string) =>
      socialApi.deactivateShareCode(group.id, shareCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group', group.id] })
      queryClient.invalidateQueries({ queryKey: ['groupShareCodes', group.id] })
    },
  })

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: (userId: string) =>
      socialApi.addGroupMember(group.id, { userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group', group.id] })
      setSelectedUserId(null)
      setSearchQuery('')
    },
  })

  const handleGenerateShareCode = () => {
    const request: GenerateShareCodeRequest = {}

    if (maxUses) {
      request.maxUses = Number(maxUses)
    }

    if (expirationDays) {
      const expirationDate = new Date()
      expirationDate.setDate(expirationDate.getDate() + Number(expirationDays))
      request.expirationDate = expirationDate.toISOString()
    }

    generateShareCodeMutation.mutate(request)
  }

  // Get current share code data (from local state or group prop)
  const currentShareCode = generatedShareCode || (group.shareCode ? {
    shareCode: group.shareCode,
    maxUses: group.shareCodeMaxUses ?? null,
    expirationDate: group.shareCodeExpirationDate ?? null,
    usedCount: group.shareCodeUsedCount ?? 0,
  } : null)

  const handleCopyShareCode = async () => {
    if (currentShareCode) {
      const shareUrl = `${window.location.origin}/code/${currentShareCode.shareCode}`
      try {
        await navigator.clipboard.writeText(shareUrl)
        setCopiedShareCode(true)
        setTimeout(() => setCopiedShareCode(false), 2000)
      } catch (err) {
        console.error('Failed to copy share code:', err)
      }
    }
  }

  const handleInviteUser = () => {
    if (selectedUserId) {
      addMemberMutation.mutate(selectedUserId)
    }
  }

  // Filter friends based on search and exclude current members
  const filteredFriends = friends?.filter(friend => {
    // Get the friend's user ID (not the connection ID)
    const friendUserId = friend.userId || friend.requesterUserId !== friend.userId ? friend.receiverUserId : friend.requesterUserId

    // Exclude if already a member
    const isMember = group.members?.some(member => member.userId === friendUserId)
    if (isMember) return false

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return (
        friend.displayName?.toLowerCase().includes(query) ||
        friend.username?.toLowerCase().includes(query)
      )
    }
    return true
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Invite Members to {group.name}
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<LinkIcon />} label="Share Code" iconPosition="start" />
        <Tab icon={<PersonAdd />} label="Invite Friends" iconPosition="start" />
      </Tabs>

      <DialogContent>
        {/* Share Code Tab */}
        {activeTab === 0 && (
          <Box>
            {/* Active Share Codes List */}
            {isLoadingShareCodes ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, mb: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : activeShareCodes && activeShareCodes.length > 0 ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Active Share Codes
                </Typography>
                <List sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  {activeShareCodes.map((code, index) => (
                    <ListItem
                      key={code.shareCode}
                      sx={{
                        borderBottom: index < activeShareCodes.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                      }}
                      secondaryAction={
                        <Tooltip title="Deactivate share code">
                          <IconButton
                            edge="end"
                            onClick={() => deactivateShareCodeMutation.mutate(code.shareCode)}
                            disabled={deactivateShareCodeMutation.isPending}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                            {code.shareCode}
                          </Typography>
                          <Tooltip title="Copy link">
                            <IconButton
                              size="small"
                              onClick={async () => {
                                const url = `${window.location.origin}/code/${code.shareCode}`
                                await navigator.clipboard.writeText(url)
                              }}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Chip
                            icon={<CalendarMonth />}
                            label={`Created ${new Date(code.createdAt).toLocaleDateString()}`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            icon={<Person />}
                            label={
                              <Link
                                to={`/profile/${code.creatorId}`}
                                style={{ color: 'inherit', textDecoration: 'none' }}
                              >
                                {code.creatorName}
                              </Link>
                            }
                            size="small"
                            variant="outlined"
                            component="div"
                          />
                          {code.maxUses !== null && (
                            <Chip
                              icon={<Numbers />}
                              label={`${code.usedCount} / ${code.maxUses} uses`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {code.expirationDate && (
                            <Chip
                              icon={<CalendarMonth />}
                              label={`Expires ${new Date(code.expirationDate).toLocaleDateString()}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {!code.maxUses && !code.expirationDate && (
                            <Chip
                              label="No restrictions"
                              size="small"
                              variant="outlined"
                              color="success"
                            />
                          )}
                        </Box>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : null}

            {currentShareCode ? (
              <>
                <Alert severity="success" sx={{ mb: 3 }}>
                  Share code is active! Share the link below with others to invite them to this group.
                </Alert>

                <TextField
                  fullWidth
                  label="Share Link"
                  value={`${window.location.origin}/code/${currentShareCode.shareCode}`}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={copiedShareCode ? 'Copied!' : 'Copy link'}>
                          <IconButton onClick={handleCopyShareCode} edge="end">
                            {copiedShareCode ? <Share color="success" /> : <ContentCopy />}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                {/* QR Code Display */}
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                  <ThemedQRCode
                    value={`${window.location.origin}/code/${currentShareCode.shareCode}`}
                    size={200}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 2 }}>
                  Scan to share this invitation
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  {currentShareCode.maxUses && (
                    <Chip
                      icon={<Numbers />}
                      label={`${currentShareCode.usedCount || 0} / ${currentShareCode.maxUses} uses`}
                      variant="outlined"
                    />
                  )}
                  {currentShareCode.expirationDate && (
                    <Chip
                      icon={<CalendarMonth />}
                      label={`Expires ${new Date(currentShareCode.expirationDate).toLocaleDateString()}`}
                      variant="outlined"
                    />
                  )}
                </Box>

                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => currentShareCode && revokeShareCodeMutation.mutate(currentShareCode.shareCode)}
                  disabled={revokeShareCodeMutation.isPending || !currentShareCode}
                  fullWidth
                >
                  Revoke Share Code
                </Button>
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Generate a share code to create an invite link. Anyone with this link can join the group.
                </Typography>

                <TextField
                  fullWidth
                  type="number"
                  label="Maximum Uses (Optional)"
                  value={maxUses}
                  onChange={(e) => setMaxUses(e.target.value ? Number(e.target.value) : '')}
                  helperText="Leave empty for unlimited uses"
                  sx={{ mb: 2 }}
                  InputProps={{ inputProps: { min: 1 } }}
                />

                <TextField
                  fullWidth
                  type="number"
                  label="Expiration (Days, Optional)"
                  value={expirationDays}
                  onChange={(e) => setExpirationDays(e.target.value ? Number(e.target.value) : '')}
                  helperText="Leave empty for no expiration"
                  sx={{ mb: 3 }}
                  InputProps={{ inputProps: { min: 1 } }}
                />

                <Button
                  variant="contained"
                  startIcon={<LinkIcon />}
                  onClick={handleGenerateShareCode}
                  disabled={generateShareCodeMutation.isPending}
                  fullWidth
                >
                  {generateShareCodeMutation.isPending ? 'Generating...' : 'Generate Share Code'}
                </Button>

                {generateShareCodeMutation.isError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    Failed to generate share code. Please try again.
                  </Alert>
                )}
              </>
            )}
          </Box>
        )}

        {/* Direct Invite Tab */}
        {activeTab === 1 && (
          <Box>
            <TextField
              fullWidth
              placeholder="Search friends..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {isLoadingFriends ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : filteredFriends && filteredFriends.length > 0 ? (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {filteredFriends.map((friend) => {
                  const friendUserId = friend.userId || (friend.requesterUserId !== friend.userId ? friend.receiverUserId : friend.requesterUserId)
                  const friendName = friend.displayName || friend.requesterName || friend.receiverName || 'Unknown'

                  return (
                    <ListItem
                      key={friend.id}
                      secondaryAction={
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PersonAdd />}
                          onClick={() => addMemberMutation.mutate(friendUserId)}
                          disabled={addMemberMutation.isPending}
                        >
                          Invite
                        </Button>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar src={friend.avatar}>
                          {friendName.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={friendName}
                        secondary={friend.username ? `@${friend.username}` : undefined}
                      />
                    </ListItem>
                  )
                })}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery ? 'No friends found matching your search' : 'No friends available to invite'}
                </Typography>
              </Box>
            )}

            {addMemberMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Failed to invite user. They may already be a member or the group is full.
              </Alert>
            )}

            {addMemberMutation.isSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                User invited successfully!
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
